(() => {
  const config = window.themeVariables?.amazon ?? {};
  const BUM_VENDOR = config.vendor ?? "BUM Energy";
  const CHECKOUT_FLUSH_DELAY_MS = 300;

  let checkoutResubmitPending = false;
  let pendingCheckoutSubmitter = null;

  function isBumVendor(vendor) {
    return typeof vendor === "string" && vendor.trim() === BUM_VENDOR;
  }

  function trackAmazon(eventName) {
    if (typeof window.amzn !== "function") {
      return;
    }

    window.amzn("trackEvent", eventName);
  }

  function getFormVendor(form) {
    return form?.dataset?.productVendor ?? form?.getAttribute("data-product-vendor");
  }

  function lineItemIsBumEnergy(item) {
    return isBumVendor(item?.properties?._vendor) || isBumVendor(item?.vendor);
  }

  function cartHasBumEnergy(cart) {
    return cart?.items?.some(lineItemIsBumEnergy) ?? false;
  }

  function isCartCheckoutForm(form) {
    if (!(form instanceof HTMLFormElement)) {
      return false;
    }

    try {
      return new URL(form.action, window.location.origin).pathname.endsWith("/cart");
    } catch {
      return form.action.includes("/cart");
    }
  }

  function isCheckoutSubmit(event) {
    return event.submitter?.name === "checkout"
      || event.submitter?.getAttribute("name") === "checkout"
      || pendingCheckoutSubmitter?.name === "checkout";
  }

  function findCheckoutButton(target) {
    return target.closest('button[name="checkout"]');
  }

  async function fetchCart() {
    const response = await fetch(`${Shopify.routes.root}cart.js`);

    if (!response.ok) {
      throw new Error("Unable to fetch cart");
    }

    return response.json();
  }

  async function resolveVendorFromAddedItem(item, form) {
    const formVendor = getFormVendor(form);

    if (isBumVendor(formVendor)) {
      return formVendor;
    }

    if (isBumVendor(item?.properties?._vendor)) {
      return item.properties._vendor;
    }

    const handle = item?.url?.split("/products/")[1]?.split("?")[0];

    if (!handle) {
      return null;
    }

    try {
      const product = await fetch(`${Shopify.routes.root}products/${handle}.js`).then((response) => response.json());
      return product.vendor;
    } catch {
      return null;
    }
  }

  async function cartContextHasBumEnergy() {
    if (config.cartHasBumProducts) {
      return true;
    }

    try {
      return cartHasBumEnergy(await fetchCart());
    } catch {
      return false;
    }
  }

  async function handleCheckoutIntent(form, submitter) {
    try {
      if (await cartContextHasBumEnergy()) {
        trackAmazon("Checkout");
      }
    } catch {
      // Allow checkout to proceed even if tracking fails.
    }

    await new Promise((resolve) => window.setTimeout(resolve, CHECKOUT_FLUSH_DELAY_MS));

    checkoutResubmitPending = true;
    pendingCheckoutSubmitter = submitter;

    if (typeof form.requestSubmit === "function" && submitter) {
      form.requestSubmit(submitter);
      return;
    }

    let hiddenCheckoutInput = form.querySelector('input[name="checkout"][data-amazon-checkout-fallback]');

    if (!hiddenCheckoutInput) {
      hiddenCheckoutInput = document.createElement("input");
      hiddenCheckoutInput.type = "hidden";
      hiddenCheckoutInput.name = "checkout";
      hiddenCheckoutInput.value = "";
      hiddenCheckoutInput.setAttribute("data-amazon-checkout-fallback", "");
      form.appendChild(hiddenCheckoutInput);
    }

    form.submit();
  }

  document.addEventListener("click", (event) => {
    const checkoutButton = findCheckoutButton(event.target);

    if (!checkoutButton) {
      return;
    }

    const form = checkoutButton.form;

    if (!form || !isCartCheckoutForm(form) || checkoutResubmitPending) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    handleCheckoutIntent(form, checkoutButton);
  }, true);

  document.addEventListener("submit", (event) => {
    const form = event.target;

    if (!isCartCheckoutForm(form) || !isCheckoutSubmit(event)) {
      return;
    }

    if (checkoutResubmitPending) {
      checkoutResubmitPending = false;
      pendingCheckoutSubmitter = null;
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    handleCheckoutIntent(form, event.submitter);
  }, true);

  if (config.isBumProductPage) {
    trackAmazon("PageView");
  }

  if (config.searchHasBumProducts) {
    trackAmazon("Search");
  }

  document.addEventListener("variant:add", async (event) => {
    const form = event.target;
    const items = event.detail?.items ?? [];
    const vendor = await resolveVendorFromAddedItem(items[0], form);

    if (!isBumVendor(vendor)) {
      return;
    }

    trackAmazon("AddToShoppingCart");

    if (items.some((item) => item.selling_plan_allocation)) {
      trackAmazon("Subscribe");
    }
  });

  document.addEventListener("click", (event) => {
    const dynamicCheckoutButton = event.target.closest(".shopify-payment-button__button, shopify-accelerated-checkout");

    if (!dynamicCheckoutButton) {
      return;
    }

    const form = dynamicCheckoutButton.closest('form[is="product-form"], form');

    if (isBumVendor(getFormVendor(form))) {
      trackAmazon("Checkout");
    }
  });

  document.addEventListener("submit", async (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    if (!form.action.includes("/contact")) {
      return;
    }

    const tagsInput = form.querySelector('[name="contact[tags]"]');

    if (!tagsInput || tagsInput.value !== "newsletter") {
      return;
    }

    if (config.isBumProductPage || config.cartHasBumProducts || await cartContextHasBumEnergy()) {
      trackAmazon("Subscribe");
    }
  }, true);

  let lastPredictiveSearchTerm = "";

  function maybeTrackPredictiveSearch() {
    const searchInput = document.querySelector('#search-drawer input[name="q"], .main-search-form input[name="q"]');

    if (!searchInput || searchInput.value.trim() === "") {
      return;
    }

    const searchTerm = searchInput.value.trim();

    if (searchTerm === lastPredictiveSearchTerm) {
      return;
    }

    const hasBumResult = document.querySelector(`predictive-search [data-product-vendor="${BUM_VENDOR}"]`);

    if (!hasBumResult) {
      return;
    }

    lastPredictiveSearchTerm = searchTerm;
    trackAmazon("Search");
  }

  document.addEventListener("input", (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.name !== "q") {
      return;
    }

    window.clearTimeout(window.__amazonPredictiveSearchTimeout);
    window.__amazonPredictiveSearchTimeout = window.setTimeout(maybeTrackPredictiveSearch, 400);
  });

  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll("predictive-search").forEach((element) => {
      const resultsSlot = element.querySelector('[slot="results"]');

      if (!resultsSlot) {
        return;
      }

      const observer = new MutationObserver(() => {
        window.clearTimeout(window.__amazonPredictiveSearchTimeout);
        window.__amazonPredictiveSearchTimeout = window.setTimeout(maybeTrackPredictiveSearch, 100);
      });

      observer.observe(resultsSlot, { childList: true, subtree: true });
    });
  });
})();
